/**
 * Context menu action definitions for different editor element types
 */
import { MenuProps } from "antd"
import {
  DeleteOutlined,
  CopyOutlined,
  UnorderedListOutlined,
  OrderedListOutlined,
  ApartmentOutlined,
  TagOutlined,
  SearchOutlined,
  RetweetOutlined,
  SwapOutlined,
  CommentOutlined,
  ClockCircleOutlined,
  NumberOutlined,
} from "@ant-design/icons"
import React from "react"
import { NodeType } from "utils/structureParser"
import {
  EditorElement,
  isGridQuestion,
  hasFromList,
  hasPerList,
} from "utils/editorElementDetection"

export type MenuItem = Required<MenuProps>["items"][number]

/**
 * Context menu action keys
 */
export enum ContextMenuAction {
  // Common actions
  EDIT_LABEL = "edit-label",
  DELETE = "delete",
  DUPLICATE = "duplicate",
  JUMP_TO_STRUCTURE = "jump-to-structure",
  JUMP_TO_LABELS = "jump-to-labels",
  ADD_COMMENT = "add-comment",
  TOGGLE_REMARK = "toggle-remark",

  // Question-specific
  INSERT_LIST_FROM = "insert-list-from",
  INSERT_LIST_PER = "insert-list-per",
  REPLACE_LIST_FROM = "replace-list-from",
  REPLACE_LIST_PER = "replace-list-per",
  CONVERT_TYPE = "convert-type",
  ADD_ROUTING = "add-routing",
  ADD_VALIDATION = "add-validation",

  // Section-specific
  EDIT_NAME = "edit-name",
  ADD_QUESTION = "add-question",
  ADD_TEXT = "add-text",
  COLLAPSE = "collapse",

  // List-specific
  EDIT_LIST = "edit-list",
  RENAME_LIST = "rename-list",
  SHOW_USAGES = "show-usages",
  RANDOMIZE = "randomize",
  CONVERT_TO_RANGE = "convert-to-range",
  CONVERT_TO_LIST = "convert-to-list",

  // Text/Note-specific
  CONVERT_TO_NOTE = "convert-to-note",
  CONVERT_TO_TEXT = "convert-to-text",

  // Logic-specific
  EDIT_CONDITION = "edit-condition",
  WRAP_IN_BEGIN = "wrap-in-begin",

  // Timing
  SET_TIMING = "set-timing",

  // Empty space
  ADD_SECTION = "add-section",
  ADD_LIST = "add-list",
  PASTE_HERE = "paste-here",
}

/**
 * Get context menu items for QUESTION elements
 */
export const getQuestionMenuItems = (element: EditorElement): MenuItem[] => {
  const isGrid = isGridQuestion(element)
  const hasFROM = hasFromList(element)
  const hasPER = hasPerList(element)

  const menuItems: MenuItem[] = [
    // {
    //   key: ContextMenuAction.EDIT_LABEL,
    //   label: 'Edit Label',
    //   icon: React.createElement(EditOutlined),
    // },
  ]

  // Add FROM list options
  if (hasFROM) {
    menuItems.push({
      key: ContextMenuAction.REPLACE_LIST_FROM,
      label: "Replace FROM list...",
      icon: React.createElement(SwapOutlined),
    })
  } else {
    menuItems.push({
      key: ContextMenuAction.INSERT_LIST_FROM,
      label: "Insert FROM list...",
      icon: React.createElement(UnorderedListOutlined),
    })
  }

  // Add PER list options (only for GRID questions or when PER already exists)
  if (isGrid) {
    if (hasPER) {
      menuItems.push({
        key: ContextMenuAction.REPLACE_LIST_PER,
        label: "Replace PER list...",
        icon: React.createElement(RetweetOutlined),
      })
    } else {
      menuItems.push({
        key: ContextMenuAction.INSERT_LIST_PER,
        label: "Insert PER list...",
        icon: React.createElement(OrderedListOutlined),
      })
    }
  }

  menuItems.push(
    {
      type: "divider",
    },
    {
      key: ContextMenuAction.SET_TIMING,
      label: "Set Timing",
      icon: React.createElement(ClockCircleOutlined),
    },
    {
      key: ContextMenuAction.DUPLICATE,
      label: "Duplicate Question",
      icon: React.createElement(CopyOutlined),
    },
    {
      type: "divider",
    },
    {
      key: ContextMenuAction.JUMP_TO_STRUCTURE,
      label: "Show in Structure",
      icon: React.createElement(ApartmentOutlined),
    },
    {
      key: ContextMenuAction.JUMP_TO_LABELS,
      label: "Edit in Labels Manager",
      icon: React.createElement(TagOutlined),
    },
    {
      type: "divider",
    },
    {
      key: ContextMenuAction.DELETE,
      label: "Delete Question",
      icon: React.createElement(DeleteOutlined),
      danger: true,
    }
  )

  return menuItems
}

/**
 * Get context menu items for SECTION elements
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getSectionMenuItems = (element: EditorElement): MenuItem[] => {
  return [
    // {
    //   key: ContextMenuAction.EDIT_NAME,
    //   label: 'Rename Section',
    //   icon: React.createElement(EditOutlined),
    // },
    {
      key: ContextMenuAction.SET_TIMING,
      label: "Set Timing",
      icon: React.createElement(ClockCircleOutlined),
    },
    {
      key: ContextMenuAction.DUPLICATE,
      label: "Duplicate Section",
      icon: React.createElement(CopyOutlined),
    },
    // {
    //   type: 'divider',
    // },
    // {
    //   key: ContextMenuAction.ADD_QUESTION,
    //   label: 'Add Question Here',
    //   icon: React.createElement(PlusOutlined),
    // },
    // {
    //   key: ContextMenuAction.ADD_TEXT,
    //   label: 'Add Text Block',
    //   icon: React.createElement(FileTextOutlined),
    // },
    {
      type: "divider",
    },
    {
      key: ContextMenuAction.JUMP_TO_STRUCTURE,
      label: "Show in Structure",
      icon: React.createElement(ApartmentOutlined),
    },
    {
      type: "divider",
    },
    {
      key: ContextMenuAction.DELETE,
      label: "Delete Section",
      icon: React.createElement(DeleteOutlined),
      danger: true,
    },
  ]
}

/**
 * Get context menu items for LIST/RANGE LIST elements
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getListMenuItems = (element: EditorElement): MenuItem[] => {
  return [
    // {
    //   key: ContextMenuAction.EDIT_LIST,
    //   label: 'Edit List Items',
    //   icon: React.createElement(EditOutlined),
    // },
    // {
    //   key: ContextMenuAction.RENAME_LIST,
    //   label: 'Rename List',
    //   icon: React.createElement(FormOutlined),
    // },
    // {
    //   type: 'divider',
    // },
    {
      key: ContextMenuAction.SHOW_USAGES,
      label: "Find Usages",
      icon: React.createElement(SearchOutlined),
      disabled: false, // Always enabled since definition always exists
    },
    // {
    //   key: ContextMenuAction.DUPLICATE,
    //   label: 'Duplicate List',
    //   icon: React.createElement(CopyOutlined),
    // },
    {
      type: "divider",
    },
    {
      key: ContextMenuAction.JUMP_TO_STRUCTURE,
      label: "Show in Structure",
      icon: React.createElement(ApartmentOutlined),
    },
    {
      key: ContextMenuAction.JUMP_TO_LABELS,
      label: "Edit in Labels Manager",
      icon: React.createElement(TagOutlined),
    },
    {
      type: "divider",
    },
    {
      key: ContextMenuAction.DELETE,
      label: "Delete List",
      icon: React.createElement(DeleteOutlined),
      danger: true,
    },
  ]
}

/**
 * Get context menu items for EXCEPTIONS elements
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getExceptionsMenuItems = (element: EditorElement): MenuItem[] => {
  return [
    // {
    //   key: ContextMenuAction.EDIT_LIST,
    //   label: 'Edit Exception Items',
    //   icon: React.createElement(EditOutlined),
    // },
    // {
    //   type: 'divider',
    // },
    {
      key: ContextMenuAction.SHOW_USAGES,
      label: "Find Usages",
      icon: React.createElement(SearchOutlined),
    },
    {
      type: "divider",
    },
    {
      key: ContextMenuAction.JUMP_TO_STRUCTURE,
      label: "Show in Structure",
      icon: React.createElement(ApartmentOutlined),
    },
    {
      key: ContextMenuAction.JUMP_TO_LABELS,
      label: "Edit in Labels Manager",
      icon: React.createElement(TagOutlined),
    },
    {
      type: "divider",
    },
    {
      key: ContextMenuAction.DELETE,
      label: "Delete Exceptions Block",
      icon: React.createElement(DeleteOutlined),
      danger: true,
    },
  ]
}

/**
 * Get context menu items for TEXT elements
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getTextMenuItems = (element: EditorElement): MenuItem[] => {
  return [
    // {
    //   key: ContextMenuAction.EDIT_LABEL,
    //   label: 'Edit Label',
    //   icon: React.createElement(EditOutlined),
    // },
    {
      key: ContextMenuAction.DUPLICATE,
      label: "Duplicate Block",
      icon: React.createElement(CopyOutlined),
    },
    // {
    //   type: 'divider',
    // },
    // {
    //   key: ContextMenuAction.CONVERT_TO_NOTE,
    //   label: 'Convert to NOTE',
    //   icon: React.createElement(SwapOutlined),
    // },
    {
      type: "divider",
    },
    {
      key: ContextMenuAction.JUMP_TO_STRUCTURE,
      label: "Show in Structure",
      icon: React.createElement(ApartmentOutlined),
    },
    {
      type: "divider",
    },
    {
      key: ContextMenuAction.DELETE,
      label: "Delete Block",
      icon: React.createElement(DeleteOutlined),
      danger: true,
    },
  ]
}

/**
 * Get context menu items for NOTE elements
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getNoteMenuItems = (element: EditorElement): MenuItem[] => {
  return [
    // {
    //   key: ContextMenuAction.EDIT_LABEL,
    //   label: 'Edit Label',
    //   icon: React.createElement(EditOutlined),
    // },
    {
      key: ContextMenuAction.DUPLICATE,
      label: "Duplicate Block",
      icon: React.createElement(CopyOutlined),
    },
    // {
    //   type: 'divider',
    // },
    // {
    //   key: ContextMenuAction.CONVERT_TO_TEXT,
    //   label: 'Convert to TEXT',
    //   icon: React.createElement(SwapOutlined),
    // },
    {
      type: "divider",
    },
    {
      key: ContextMenuAction.JUMP_TO_STRUCTURE,
      label: "Show in Structure",
      icon: React.createElement(ApartmentOutlined),
    },
    {
      type: "divider",
    },
    {
      key: ContextMenuAction.DELETE,
      label: "Delete Block",
      icon: React.createElement(DeleteOutlined),
      danger: true,
    },
  ]
}

/**
 * Get context menu items for LOGIC elements
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getLogicMenuItems = (element: EditorElement): MenuItem[] => {
  return [
    // {
    //   key: ContextMenuAction.EDIT_CONDITION,
    //   label: 'Edit Condition',
    //   icon: React.createElement(EditOutlined),
    // },
    // {
    //   type: 'divider',
    // },
    {
      key: ContextMenuAction.JUMP_TO_STRUCTURE,
      label: "Show in Structure",
      icon: React.createElement(ApartmentOutlined),
    },
    {
      type: "divider",
    },
    {
      key: ContextMenuAction.DELETE,
      label: "Delete Logic Block",
      icon: React.createElement(DeleteOutlined),
      danger: true,
    },
  ]
}

/**
 * Get context menu items for empty space (no element selected)
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getEmptySpaceMenuItems = (hasClipboard = false): MenuItem[] => {
  return [
    // {
    //   key: ContextMenuAction.ADD_QUESTION,
    //   label: 'Add Question',
    //   icon: React.createElement(PlusOutlined),
    // },
    // {
    //   key: ContextMenuAction.ADD_SECTION,
    //   label: 'Add Section',
    //   icon: React.createElement(FolderAddOutlined),
    // },
    // {
    //   key: ContextMenuAction.ADD_LIST,
    //   label: 'Add List',
    //   icon: React.createElement(UnorderedListOutlined),
    // },
    // {
    //   key: ContextMenuAction.ADD_TEXT,
    //   label: 'Add Text Block',
    //   icon: React.createElement(FileTextOutlined),
    // },
    // {
    //   type: 'divider',
    // },
    // {
    //   key: ContextMenuAction.PASTE_HERE,
    //   label: 'Paste',
    //   icon: React.createElement(SnippetsOutlined),
    //   disabled: !hasClipboard,
    // },
  ]
}

/**
 * Get the "Add Comment" menu item
 */
export const getAddCommentMenuItem = (): MenuItem => ({
  key: ContextMenuAction.ADD_COMMENT,
  label: "Add Comment",
  icon: React.createElement(CommentOutlined),
})

/**
 * Get the "Toggle Remark" menu item. Adds or removes a `#` prefix on the
 * affected line(s) so they're treated as remarks (ignored by parser).
 */
export const getToggleRemarkMenuItem = (isMultiLine: boolean): MenuItem => ({
  key: ContextMenuAction.TOGGLE_REMARK,
  label: isMultiLine ? "Toggle Remark on Lines" : "Toggle Remark",
  icon: React.createElement(NumberOutlined),
})

/**
 * Get context menu items based on element type
 */
export const getContextMenuItems = (
  element: EditorElement | null,
  hasClipboard = false,
  hasSelectedText = false
): MenuItem[] => {
  const items: MenuItem[] = []

  // Always add "Add Comment" option when text is selected
  if (hasSelectedText) {
    items.push(getAddCommentMenuItem())
  }

  // Always offer the remark toggle — works for cursor-on-line or selection
  items.push(getToggleRemarkMenuItem(hasSelectedText))

  if (!element) {
    const emptyItems = getEmptySpaceMenuItems(hasClipboard)
    if (items.length > 0 && emptyItems.length > 0) {
      items.push({ type: "divider" })
    }
    return [...items, ...emptyItems]
  }

  // Add divider if we have the comment option
  if (items.length > 0) {
    items.push({ type: "divider" })
  }

  let elementItems: MenuItem[] = []
  switch (element.type) {
    case NodeType.QUESTION:
      elementItems = getQuestionMenuItems(element)
      break
    case NodeType.SECTION:
      elementItems = getSectionMenuItems(element)
      break
    case NodeType.LIST:
    case NodeType.RANGE_LIST:
      elementItems = getListMenuItems(element)
      break
    case NodeType.EXCEPTIONS:
      elementItems = getExceptionsMenuItems(element)
      break
    case NodeType.TEXT:
      elementItems = getTextMenuItems(element)
      break
    case NodeType.NOTE:
      elementItems = getNoteMenuItems(element)
      break
    case NodeType.LOGIC:
      elementItems = getLogicMenuItems(element)
      break
    default:
      elementItems = getEmptySpaceMenuItems(hasClipboard)
  }

  return [...items, ...elementItems]
}
